import Form from 'antd/lib/form';
import * as React from 'react';
import {
    AllDaysOfTheWeek,
    SettingField,
    AutoStagedRolloutBlackoutDay,
    AutoStagedRolloutCadence,
    DayOfTheWeek,
} from '../../types';
import { ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import TimePicker from 'antd/lib/time-picker';
import { Link } from 'react-router-dom';
import { decodeAutoStagedRolloutBlackoutSegmentsByDay } from '../../helpers';
import { displayTextForDayOfTheWeek } from '../../helpers/displayHelpers';
import { FieldType, FormFieldData } from '../../helpers/formHelpers';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import { runwayColors } from '../styles/globalStyles';
import Tooltip from 'antd/lib/tooltip';
import { DEFAULT_ROLLOUT_SEQUENCE } from '../../constants';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { AndroidStagedRolloutPercentageInput } from '../sharedComponents/shared';

export const buildDaysToPercentField = (
    initialValue: number[],
    mustEndAtOneHundred = false,
    initialCadence = AutoStagedRolloutCadence.Daily,
    expoInitialRolloutPercentage?: number,
    expoInitialRolloutSettingsLink?: string,
): FormFieldData => ({
    name: 'daysToPercent',
    fieldType: FieldType.List,
    initialValue: withExpoInitialRolloutPercentage(initialValue, expoInitialRolloutPercentage),
    rules: [
        {
            validator: async (_, values) => {
                if (!values || values?.length === 0) {
                    return Promise.reject(new Error('Percentages need at least 1 day'));
                }
                if (values.length === 1) {
                    return Promise.resolve();
                }
                const numericValues = values.map((value) => Number(value));
                for (let i = 0; i < values.length; i++) {
                    if (numericValues[i - 1] > numericValues[i]) {
                        return Promise.reject(new Error('Percentages must increase over rollout'));
                    }
                }
                if (numericValues.includes(0)) {
                    return Promise.reject(new Error('Percentages must be greater than 0'));
                }
                if (mustEndAtOneHundred && numericValues[numericValues.length - 1] !== 100) {
                    return Promise.reject(new Error('Expo rollout schedules must end at 100%'));
                }
                return Promise.resolve();
            },
        },
    ],
    listChildrenWrapper: (children, _, { errors }) => {
        return (
            <Space
                direction="vertical"
                style={{
                    width: '100%',
                }}
                size={16}
            >
                <Form.Item label="Number of increments" rules={[{ required: true }]} required shouldUpdate>
                    {(form) => {
                        return (
                            <Select
                                defaultValue={initialValue.length}
                                style={{ width: 120 }}
                                onChange={(value) => {
                                    const selectedDays = Math.trunc(value);
                                    const sequence = [...DEFAULT_ROLLOUT_SEQUENCE].reverse();
                                    const updatedSequence: number[] = [];

                                    [...Array(selectedDays)].forEach((_, i) => {
                                        updatedSequence.push(sequence[selectedDays - i - 1]);
                                    });

                                    form.setFieldsValue({
                                        daysToPercent: withExpoInitialRolloutPercentage(
                                            updatedSequence,
                                            expoInitialRolloutPercentage,
                                        ),
                                    });
                                }}
                            >
                                {[...DEFAULT_ROLLOUT_SEQUENCE].slice(1).map((_, i) => {
                                    const day = i + 2;
                                    return (
                                        <Select.Option value={day} key={i}>
                                            {day}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        );
                    }}
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                    {(form) => {
                        const daysToPercent = form.getFieldValue('daysToPercent');
                        const cadence = form.getFieldValue('incrementCadence') ?? initialCadence;
                        if (!daysToPercent || daysToPercent.length === 0) {
                            return null;
                        }
                        return (
                            <Form.Item
                                label={`Rollout percentage per ${cadence === AutoStagedRolloutCadence.Hourly ? 'hour' : 'day'}`}
                                required
                                style={{
                                    marginBottom: 0,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(3, 1fr)`,
                                    }}
                                >
                                    {children}
                                </div>
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <div
                    style={{
                        color: runwayColors.statusRed,
                    }}
                >
                    <Form.ErrorList errors={errors} />
                </div>
            </Space>
        );
    },
    nestedListFields: [
        {
            fieldType: FieldType.Custom,
            renderField: ({ index, listFieldData }) => {
                const increment = index + 1;
                const isExpoInitialIncrement = index === 0 && expoInitialRolloutPercentage !== undefined;
                const percentageFormItem = (
                    <Form.Item
                        noStyle
                        validateTrigger="onSubmit"
                        {...listFieldData}
                        style={{
                            marginBottom: 8,
                        }}
                        rules={[
                            {
                                required: true,
                                message: `Increment ${increment} is required`,
                            },
                            {
                                validator(_rule, value) {
                                    if (value === 0 || value > 100) {
                                        return Promise.reject();
                                    }

                                    return Promise.resolve();
                                },
                                message:
                                    'Staged rollout percentage must be greater than zero and less than or equal to 100. Up to 6 decimal points are allowed.',
                            },
                        ]}
                        hasFeedback={false}
                    >
                        <AndroidStagedRolloutPercentageInput
                            disabled={isExpoInitialIncrement}
                            style={{
                                width: 140,
                            }}
                        />
                    </Form.Item>
                );
                return (
                    <Space size={6}>
                        <Form.Item
                            noStyle
                            shouldUpdate={(previous, current) => previous.incrementCadence !== current.incrementCadence}
                        >
                            {(form) => {
                                const cadence = form.getFieldValue('incrementCadence') ?? initialCadence;
                                return (
                                    <Text>
                                        {cadence === AutoStagedRolloutCadence.Hourly ? 'Hour' : 'Day'} {increment}
                                    </Text>
                                );
                            }}
                        </Form.Item>
                        {isExpoInitialIncrement ?
                            <Tooltip
                                title={
                                    <span>
                                        Set the initial rollout percentage in the{' '}
                                        {expoInitialRolloutSettingsLink ?
                                            <Link
                                                to={expoInitialRolloutSettingsLink}
                                                style={{ color: 'white', textDecoration: 'underline' }}
                                            >
                                                release step settings
                                            </Link>
                                        :   'release step settings'}
                                        .
                                    </span>
                                }
                            >
                                <span>{percentageFormItem}</span>
                            </Tooltip>
                        :   percentageFormItem}
                    </Space>
                );
            },
        },
    ],
});

export const withExpoInitialRolloutPercentage = (
    rolloutPercentages: number[],
    expoInitialRolloutPercentage?: number,
): number[] => {
    if (expoInitialRolloutPercentage === undefined) {
        return rolloutPercentages;
    }

    return [expoInitialRolloutPercentage, ...rolloutPercentages.slice(1)];
};

// Its help text heads the days, above the field rather than below it
export const buildBlackoutScheduleField = (settingField: SettingField): FormFieldData => ({
    fieldType: FieldType.Custom,
    initialValue: decodeAutoStagedRolloutBlackoutSegmentsByDay(settingField.currentValue),
    name: settingField.key,
    extraFormItemProps: {
        name: undefined, // this will override the name in the field which fixes a bug with checkbox overriding the whole object value
    },
    showDivider: false,
    renderField: () => {
        return (
            <>
                <Text>{settingField.helpText}</Text>
                <Space
                    direction="vertical"
                    style={{
                        width: '100%',
                        marginTop: 16,
                    }}
                    split={
                        <Divider
                            style={{
                                margin: '8px 0',
                            }}
                        />
                    }
                >
                    {AllDaysOfTheWeek.map((day) => (
                        <React.Fragment key={day}>{generateDayRow(settingField.key, day)}</React.Fragment>
                    ))}
                </Space>
            </>
        );
    },
});

function generateDayRow(prefix: string, day: DayOfTheWeek) {
    const isBlackedOutKey: keyof AutoStagedRolloutBlackoutDay = 'isBlackedOut';
    return (
        <Form.Item shouldUpdate noStyle>
            {(form) => {
                const isBlackedOut = form.getFieldValue([prefix, day, isBlackedOutKey]);
                return (
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                        }}
                    >
                        <Space align="center" size={10}>
                            <Form.Item name={[prefix, day, 'isBlackedOut']} noStyle valuePropName="checked">
                                <Checkbox>
                                    <Text strong={isBlackedOut}>{displayTextForDayOfTheWeek(day)}</Text>
                                </Checkbox>
                            </Form.Item>
                        </Space>

                        <Form.List name={[prefix, day, 'segments']}>
                            {(fields, { add, remove }) => (
                                <div
                                    style={{
                                        display: isBlackedOut ? 'flex' : 'none',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        gap: 8,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 8,
                                        }}
                                    >
                                        {fields.length === 0 && (
                                            <Text
                                                type="secondary"
                                                disabled={!isBlackedOut}
                                                style={{
                                                    width: 206,
                                                }}
                                            >
                                                All day
                                            </Text>
                                        )}
                                        {fields.map((fieldData, i) => {
                                            return (
                                                <div
                                                    key={fieldData.key}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'baseline',
                                                        gap: 8,
                                                    }}
                                                >
                                                    <Form.Item
                                                        name={[fieldData.name, 'blackoutSegment']}
                                                        style={{
                                                            marginBottom: 0,
                                                        }}
                                                        rules={[
                                                            {
                                                                validator: async (_, segment) => {
                                                                    if (!isBlackedOut) {
                                                                        return Promise.resolve();
                                                                    }

                                                                    if (!segment || !segment[0] || !segment[1]) {
                                                                        return Promise.reject('Invalid segment');
                                                                    }

                                                                    return Promise.resolve();
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        <TimePicker.RangePicker
                                                            disabled={!isBlackedOut}
                                                            placeholder={['Start', 'End']}
                                                            minuteStep={10}
                                                            order={false}
                                                            format="h:mm a"
                                                            style={{
                                                                width: 206,
                                                            }}
                                                        />
                                                    </Form.Item>
                                                    <Button
                                                        icon={
                                                            <DeleteOutlined
                                                                color={runwayColors.statusRed}
                                                                disabled={!isBlackedOut}
                                                            />
                                                        }
                                                        disabled={!isBlackedOut}
                                                        size="small"
                                                        danger
                                                        type="link"
                                                        onClick={() => remove(i)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <Button
                                        icon={
                                            <ClockCircleOutlined
                                                color={runwayColors.primary}
                                                disabled={!isBlackedOut}
                                            />
                                        }
                                        disabled={!isBlackedOut}
                                        type="link"
                                        size="small"
                                        onClick={() => add()}
                                    >
                                        Add times
                                    </Button>
                                </div>
                            )}
                        </Form.List>
                    </div>
                );
            }}
        </Form.Item>
    );
}
